// Isolated bill printing — clones the target DOM node into a hidden iframe
// and prints only the iframe. This bypasses the whole-page window.print()
// problem where the sidebar, buttons, and other page chrome were included.
//
// Usage:
//   import { printNode } from "@/lib/printBill";
//   printNode(receiptRef.current, { title: "Bill BPKD/4012", pageSize: "a4" });
//
// pageSize: "a4" (default) or "thermal80" for 80mm thermal printers.

const A4_STYLE = `
  @page { size: A4; margin: 10mm; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Manrope, Arial, sans-serif; color: #2A2624; background: #fff; }
`;
const THERMAL_STYLE = `
  @page { size: 80mm auto; margin: 3mm; }
  body { font-family: "Courier New", monospace; color: #000; background: #fff; width: 74mm; }
  * { font-size: 11px !important; line-height: 1.25 !important; }
`;

function collectHeadStyles() {
  // Copy every <style> and same-origin <link rel=stylesheet> so Tailwind
  // classes on the cloned node still resolve inside the iframe.
  const nodes = document.querySelectorAll('link[rel="stylesheet"], style');
  return Array.from(nodes).map((n) => n.outerHTML).join("\n");
}

export function printNode(node, { title = "Print", pageSize = "a4" } = {}) {
  if (!node) return;
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.setAttribute("aria-hidden", "true");
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument || iframe.contentWindow.document;
  const inlineStyle = pageSize === "thermal80" ? THERMAL_STYLE : A4_STYLE;
  doc.open();
  doc.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${title.replace(/</g, "&lt;")}</title>
    ${collectHeadStyles()}
    <style>${inlineStyle}
      /* Force the cloned content visible even if the original had .hidden. */
      #__print_root__ { display: block !important; }
      #__print_root__ .hidden { display: block !important; }
      /* Never carry over sidebars/toolbars accidentally. */
      button, [data-testid$="-close"], [data-testid$="-print"] { display: none !important; }
    </style>
  </head>
  <body>
    <div id="__print_root__"></div>
  </body>
</html>`);
  doc.close();

  const inject = () => {
    const clone = node.cloneNode(true);
    // Remove any print:hidden / hidden helpers so the cloned printable content is guaranteed to render.
    clone.classList.remove("hidden");
    doc.getElementById("__print_root__").appendChild(clone);
    // Wait a tick for styles/images to layout, then print.
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } finally {
        setTimeout(() => document.body.removeChild(iframe), 500);
      }
    }, 250);
  };

  // If the iframe already has body, inject immediately, otherwise wait for load.
  if (doc.readyState === "complete") inject();
  else iframe.addEventListener("load", inject, { once: true });
}
